import { useState } from "react";
import { toast } from "sonner";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { useDebounce } from "@/hooks/use-debounce";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MarkdownProse } from "@/components/ui/markdown-prose";
import { Skeleton } from "@/components/ui/skeleton";

interface CreateIssueDialogProps {
  canonicalId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Preview and create the GitHub issue for one finding: editable title,
 * section toggles, the body exactly as it will be filed. The server
 * dedupes, so a second click reports "already creating".
 */
export function CreateIssueDialog({
  canonicalId,
  open,
  onOpenChange,
}: CreateIssueDialogProps) {
  const utils = trpc.useUtils();
  const [title, setTitle] = useState<string | null>(null);
  const [quote, setQuote] = useState(true);
  const [evidence, setEvidence] = useState(true);
  const debouncedTitle = useDebounce(title ?? "", 300);
  const sections = { personaQuote: quote, evidence };

  const preview = trpc.workflow.previewIssue.useQuery(
    {
      canonicalId,
      title: debouncedTitle || undefined,
      sections,
    },
    { enabled: open, placeholderData: (prev) => prev },
  );

  const create = trpc.workflow.createIssue.useMutation({
    onSuccess: (res) => {
      if (res.status === "created") {
        toast.success(`Created issue ${res.displayKey}`, {
          action: res.url
            ? {
                label: "View",
                onClick: () => window.open(res.url ?? "", "_blank"),
              }
            : undefined,
        });
      } else if (res.status === "exists") {
        toast.info(`Already linked to issue ${res.displayKey}`);
      } else {
        toast.info("That issue is already being created");
      }
      utils.workflow.get.invalidate({ canonicalId });
      utils.workflow.timeline.invalidate({ canonicalId });
      onOpenChange(false);
    },
    onError: (err) => {
      toastTrpcError(err, "Failed to create issue");
      utils.workflow.get.invalidate({ canonicalId });
    },
  });

  const shownTitle = title ?? preview.data?.title ?? "";
  const targetLabel = preview.data?.target || preview.data?.repo;
  const provider = (preview.data as any)?.provider;
  const providerTitle =
    provider === "jira"
      ? "Jira"
      : provider === "azure_devops"
        ? "Azure DevOps"
        : provider === "linear"
          ? "Linear"
          : "GitHub";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create {providerTitle} issue</DialogTitle>
          <DialogDescription>
            {targetLabel
              ? `Files in ${targetLabel}. Closing it marks the finding “awaiting re-run”.`
              : "Loading the issue preview…"}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate({
              canonicalId,
              title: shownTitle.trim() || undefined,
              sections,
            });
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="issue-title">Title</Label>
            <Input
              id="issue-title"
              value={shownTitle}
              maxLength={256}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <fieldset className="space-y-3">
            <legend className="text-sm font-medium">Include</legend>
            <div className="flex items-center gap-2">
              <Checkbox
                id="issue-quote"
                checked={quote}
                onCheckedChange={(v) => setQuote(v === true)}
              />
              <Label htmlFor="issue-quote">
                The persona's words at the step
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="issue-evidence"
                checked={evidence}
                onCheckedChange={(v) => setEvidence(v === true)}
              />
              <Label htmlFor="issue-evidence">
                A link to the step screenshot (sign-in required)
              </Label>
            </div>
          </fieldset>
          <div className="space-y-2">
            <p className="text-sm font-medium" id="issue-preview-label">
              Body
            </p>
            <section
              aria-labelledby="issue-preview-label"
              className="rounded-md border border-border bg-background p-4"
            >
              {preview.data ? (
                <MarkdownProse
                  content={preview.data.body}
                  className="text-sm text-foreground"
                />
              ) : (
                <Skeleton className="h-40 w-full" />
              )}
            </section>
            {preview.data && (
              <p className="text-xs text-muted-foreground">
                Labels: {preview.data.labels.join(", ")}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending || !targetLabel}>
              {create.isPending ? "Creating…" : "Create issue"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface LinkExistingDialogProps {
  canonicalId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Link an existing tracker issue or pull request by its URL or key. */
export function LinkExistingDialog({
  canonicalId,
  open,
  onOpenChange,
}: LinkExistingDialogProps) {
  const utils = trpc.useUtils();
  const [url, setUrl] = useState("");
  const link = trpc.workflow.linkExisting.useMutation({
    onSuccess: (res) => {
      toast.success(`Linked ${res.displayKey}`);
      utils.workflow.get.invalidate({ canonicalId });
      utils.workflow.timeline.invalidate({ canonicalId });
      setUrl("");
      onOpenChange(false);
    },
    onError: (err) => toastTrpcError(err, "Failed to link issue"),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Link existing work</DialogTitle>
          <DialogDescription>
            Paste a GitHub issue or pull request URL, or a Jira issue URL / key
            (e.g. PROJ-123). Its state then moves this finding.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (url.trim()) link.mutate({ canonicalId, url: url.trim() });
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="link-url">Tracker URL or issue key</Label>
            <Input
              id="link-url"
              type="text"
              autoComplete="off"
              placeholder="https://github.com/... or PROJ-123"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={link.isPending || !url.trim()}>
              {link.isPending ? "Linking…" : "Link"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
